// @need-translate
import {produce} from 'immer';
import {useReducer} from 'react';
import {t} from '../../../../common/getTranslations';
import {twMerge} from '../../../../common/twMerge';
import {Button} from '../../../../design-system/atoms/Button/Button';
import {
  ButtonColorScheme,
  ButtonSize,
} from '../../../../design-system/atoms/Button/Button.enum';
import {Divider} from '../../../../design-system/atoms/Divider/Divider';
import {Icon} from '../../../../design-system/atoms/Icon/Icon';
import {IconType} from '../../../../design-system/atoms/Icon/IconType.enum';
import {Text} from '../../../../design-system/atoms/Text/Text';
import {Caption} from '../../../../design-system/molecules/Caption/Caption';
import {CaptionType} from '../../../../design-system/molecules/Caption/CaptionProps.interface';
import {Check} from '../../../../design-system/molecules/Check/Check';
import {HeaderTitle} from '../../../../design-system/organisms/HeaderTitle/HeaderTitle';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {theme} from '../../../../theme';
import {RefundableAmountExistsCheckerHeadless} from '../../../common/headless/RefundableAmountExistsCheckerHeadless';
import {RefundableAmountInfo} from '../../../common/presentational/RefundableAmountInfo';
import {CreditRefundAgreementProps, RefundAgreementState} from '../type';

export function CreditRefundAgreement({
  refundableAmount,
  refundCreditInfoRowList,
  onClickNext,
  isMobile,
}: CreditRefundAgreementProps) {
  const [refundAgreementState, updateRefundAgreementState] = useReducer<
    (
      prev: RefundAgreementState,
      next: Partial<RefundAgreementState>
    ) => RefundAgreementState
  >(
    (prev, next) => {
      const updated = {...prev, ...next};

      return produce(updated, draft => {
        draft.isCheckedAll =
          updated.warningAgreement && updated.personalInfoAgreement;
      });
    },
    {
      warningAgreement: false,
      openedWarningInfo: true,
      openedPersonalInfo: true,
      personalInfoAgreement: false,
      isCheckedAll: false,
    }
  );

  return (
    <div
      className={twMerge(
        'flex h-full w-full grow flex-col bg-theme-bg-base',
        isMobile ? 'pb-76px' : 'pb-84px'
      )}
      {...putTiaraObjectProps({
        click: {layer1: '고객센터'},
      })}
    >
      {!isMobile && (
        <HeaderTitle className="mb-32pxr mt-20pxr px-15px">
          {t('more_customer_cash_refund')}
        </HeaderTitle>
      )}
      <RefundableAmountInfo
        className="px-15px py-20px"
        refundableAmount={refundableAmount}
        refundCreditInfoRowList={refundCreditInfoRowList}
      />
      <Divider sizePx={4} color={theme.colors['theme-solid-10']} />
      <RefundableAmountExistsCheckerHeadless
        refundableAmount={refundableAmount}
        isMobile={isMobile}
      >
        <div className="py-20px">
          <label className="flex items-center">
            <Check
              checked={refundAgreementState.isCheckedAll}
              onChangeChecked={() => {
                updateRefundAgreementState({
                  warningAgreement: !refundAgreementState.isCheckedAll,
                  personalInfoAgreement: !refundAgreementState.isCheckedAll,
                });
              }}
              label={t('signup_terms_allagree')}
              isTitle={true}
            />
          </label>
          <div className="mt-12px">
            <Check
              checked={refundAgreementState.warningAgreement}
              onChangeChecked={() =>
                updateRefundAgreementState({
                  warningAgreement: !refundAgreementState.warningAgreement,
                })
              }
              label={t('cash_refund_policy_agree_checkbox')}
              isTitle={false}
            />
            <div className="px-15px py-8px">
              <div className="box-border rounded-[2px] border-1 border-theme-transparent-10">
                <div className="flex flex-col gap-8px bg-theme-solid-10 p-10px">
                  <Caption
                    type={CaptionType.Normal}
                    label={t('cash_refund_policy_1')}
                  />
                  <Caption
                    type={CaptionType.Normal}
                    label={t('cash_refund_policy_2')}
                  />
                  <Caption
                    type={CaptionType.Normal}
                    label={t('cash_refund_policy_3')}
                  />
                  {refundAgreementState.openedWarningInfo && (
                    <>
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_4')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_5')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_6')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_7')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_8')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_9')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_10')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_11')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_policy_12')}
                      />
                    </>
                  )}
                </div>
                <div
                  onClick={() =>
                    updateRefundAgreementState({
                      openedWarningInfo:
                        !refundAgreementState.openedWarningInfo,
                    })
                  }
                  className="box-border flex h-28px cursor-pointer items-center justify-center border-t-1 border-theme-transparent-10 bg-theme-solid-10"
                >
                  <Icon
                    type={
                      refundAgreementState.openedWarningInfo
                        ? IconType.TabArrowUp
                        : IconType.TabArrowDown
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8pxr">
            <Check
              checked={refundAgreementState.personalInfoAgreement}
              onChangeChecked={() =>
                updateRefundAgreementState({
                  personalInfoAgreement:
                    !refundAgreementState.personalInfoAgreement,
                })
              }
              label={t('cash_refund_privacy_policy_agree_checkbox')}
              isTitle={false}
            />
            <div className="px-15px py-8px">
              <div className="box-border rounded-[2px] border-1 border-theme-transparent-10">
                <div className="flex flex-col gap-8px bg-theme-solid-10 p-10px">
                  <Text className="font-small2 text-theme-solid-60">
                    {t('cash_refund_privacy_policy_1')}
                  </Text>
                  {refundAgreementState.openedPersonalInfo && (
                    <>
                      <Text className="font-small2 text-theme-solid-60">
                        {t('cash_refund_privacy_policy_2')}
                      </Text>
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_privacy_policy_3')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_privacy_policy_4')}
                      />
                      <Caption
                        type={CaptionType.Normal}
                        label={t('cash_refund_privacy_policy_5')}
                        boldDetail={t('cash_refund_privacy_policy_5_bold')}
                      />
                      <Text className="font-small2 text-theme-solid-60">
                        {t('cash_refund_privacy_policy_6')}
                      </Text>
                    </>
                  )}
                </div>
                <div
                  onClick={() =>
                    updateRefundAgreementState({
                      openedPersonalInfo:
                        !refundAgreementState.openedPersonalInfo,
                    })
                  }
                  className="box-border flex h-28px cursor-pointer items-center justify-center border-t-1 border-theme-transparent-10 bg-theme-solid-10"
                >
                  <Icon
                    type={
                      refundAgreementState.openedPersonalInfo
                        ? IconType.TabArrowUp
                        : IconType.TabArrowDown
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={twMerge(
            'mx-auto w-full',
            isMobile && 'fixed inset-x-0 bottom-0',
            !isMobile && 'px-15px'
          )}
        >
          <Button
            className={twMerge('font-medium1-bold block')}
            colorScheme={ButtonColorScheme['1st']}
            disabled={!refundAgreementState.isCheckedAll}
            onClick={onClickNext}
            size={isMobile ? ButtonSize.BottomFixed : ButtonSize.Large}
            bottomSafeAreaPaddingNeeded
          >
            {t('common_next_button')}
          </Button>
        </div>
      </RefundableAmountExistsCheckerHeadless>
    </div>
  );
}
